/**
 * Mobile sidebar preference bootstrapper (namespaced + versioned)
 *
 * Writes localStorage keys only on mobile/tablet/capacitor clients as flagged by
 * `<html data-device-type="...">` (set server-side by DeviceUtil.php).
 *
 * Keys written (all namespaced under `n-ai.sidebar.*`):
 *  - n-ai.sidebar.prefVersion : "1" (string) — bump to invalidate old prefs
 *  - n-ai.sidebar.initialized : "true" when defaults have been seeded
 *  - n-ai.sidebar.isOpen      : "false" (default closed)
 *
 * Notes:
 *  - Values are stored as strings. When reading elsewhere, compare explicitly,
 *    e.g., `localStorage.getItem(KEYS.IS_OPEN) === 'true'`.
 *  - In dev builds, this file logs minimal debug info to help diagnose why
 *    initialization did or didn’t run.
 */

// =============================================================================
// CONSTANTS
// =============================================================================
const KEY_PREFIX = 'n-ai.sidebar.';
const KEYS = Object.freeze({
    PREF_VERSION: `${KEY_PREFIX}prefVersion`,
    INITIALIZED: `${KEY_PREFIX}initialized`,
    IS_OPEN: `${KEY_PREFIX}isOpen`,
});

// Bump this to invalidate stored defaults across releases
const PREF_VERSION = '1';

// Allowed device types coming from <html data-device-type="…"> (server-side)
const ALLOWED_DEVICE_TYPES = ['mobile', 'tablet', 'capacitor'];

// Default preference values
const DEFAULTS = Object.freeze({
    IS_OPEN: 'false', // default closed on mobile
});

// Detect dev mode safely (works with Vite; benign elsewhere)
const IS_DEV = (() => {
    try {
        return !!(import.meta && import.meta.env && import.meta.env.DEV);
    } catch (_) {
        return false;
    }
})();

// =============================================================================
// UTILITIES
// =============================================================================
function debug(...args) {
    if (IS_DEV && typeof console !== 'undefined' && console.debug) {
        console.debug('[mobile-sidebar-check]', ...args);
    }
}

function localStorageAvailable() {
    if (typeof Storage === 'undefined') {
        debug('localStorage not supported');
        return false;
    }
    const testKey = '__ls_test__';
    try {
        localStorage.setItem(testKey, '1');
        localStorage.removeItem(testKey);
        return true;
    } catch (e) {
        debug('localStorage not available (private mode or quota)', e && e.message);
        return false;
    }
}

function isMobileLikeDevice() {
    const deviceType = (document.documentElement.dataset.deviceType || '').toLowerCase();
    const isMobile = ALLOWED_DEVICE_TYPES.includes(deviceType);
    if (!isMobile) {
        debug(`Not a mobile-like device (data-device-type="${deviceType}")`);
    }
    return isMobile;
}

// =============================================================================
// INITIALIZER
// =============================================================================
function initMobileSidebar() {
    try {
        if (!localStorageAvailable()) return;
        if (!isMobileLikeDevice()) return;

        const storedVersion = localStorage.getItem(KEYS.PREF_VERSION);

        // If version mismatches, reset defaults and mark initialized
        if (storedVersion !== PREF_VERSION) {
            debug(`Seeding defaults (version ${PREF_VERSION}; was ${storedVersion || 'none'})`);
            localStorage.setItem(KEYS.IS_OPEN, DEFAULTS.IS_OPEN);
            localStorage.setItem(KEYS.INITIALIZED, 'true');
            localStorage.setItem(KEYS.PREF_VERSION, PREF_VERSION);
            return; // done for this pass
        }

        // First-time init for current version
        const initialized = localStorage.getItem(KEYS.INITIALIZED) === 'true';
        if (!initialized) {
            debug('First-time initialization (same version)');
            localStorage.setItem(KEYS.IS_OPEN, DEFAULTS.IS_OPEN);
            localStorage.setItem(KEYS.INITIALIZED, 'true');
        } else {
            debug('Already initialized; leaving user prefs as-is');
        }
    } catch (e) {
        // Fail silently for end users; log in dev
        debug('Initialization error', e && e.message);
    }
}

// Run on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMobileSidebar);
} else {
    initMobileSidebar();
}
